import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MISSION, VISION, VALUES } from '../constants/info';
import styled from 'styled-components';
import kidsDancing2 from '../assets/img/kids-dancing-2.jpg';
import ourSpace2 from '../assets/img/our-space-2.jpg';
import ourSpace4 from '../assets/img/our-space-4.jpg';
import kidsAfrica2 from '../assets/img/kids-africa-2.jpg';
import marioPiedade from '../assets/img/Mario Piedade - Director.jpeg';
import elizabeteVeloso from '../assets/img/Elizabete Veloso - Coordenadora Ensino Primario.jpeg';
import carlaCampos from '../assets/img/Carla Campos - Coordenadora Iº Ciclo.jpeg';
import '../assets/css/Home.css';

// Define theme colors for consistency
const theme = {
  primaryBlue: '#3498db',
  primaryPink: '#e91e63',
  primaryGreen: '#4caf50',
  primaryOrange: '#ff9800',
  primaryRed: '#f44336',
  primaryYellow: '#ffeb3b',
  white: '#ffffff',
  lightGray: '#f8f9fa',
  darkText: '#333333',
  mediumText: '#666666',
};

// Styled Components para um look profissional e moderno
// Componente de card de valor aprimorado
const ValueCard = styled(motion.div)`
  background: ${theme.white};
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  padding: 2.5rem 2.5rem;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: 5px solid transparent;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    border-top-color: ${props => props.accentColor};
  }

  .value-icon {
    width: 70px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${props => props.accentColor}25;
    border-radius: 50%;
    margin-bottom: 1.8rem;
    font-size: 2rem;
    color: ${props => props.accentColor};
  }

  .value-title {
    font-weight: 700;
    font-size: 1.6rem;
    margin-bottom: 1.2rem;
    color: ${theme.darkText};
    position: relative;
    padding-bottom: 12px;
  }

  .value-title:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: ${props => props.accentColor};
    border-radius: 2px;
  }

  .value-description {
    color: ${theme.mediumText};
    line-height: 1.8;
    font-size: 1.1rem;
    flex-grow: 1;
    letter-spacing: 0.01em;
  }
`;

const SectionContainer = styled.section`
  padding: 5rem 2rem;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 6rem 3rem;
  }
`;

const SectionTitle = styled.h2.withConfig({
  shouldForwardProp: (prop) => prop !== 'accentColor'
})`
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 2.2rem;
  margin-bottom: 2.5rem;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${props => props.accentColor || theme.primaryBlue};
    border-radius: 2px;
  }
`;

const SectionIntro = styled.p`
  font-size: 1.1rem;
  color: ${theme.mediumText};
  line-height: 1.8;
  margin-bottom: 3rem;
  letter-spacing: 0.01em;
`;

const StoryCard = styled(motion.div).withConfig({
  shouldForwardProp: (prop) => prop !== 'accentColor'
})`
  background: ${theme.white};
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: ${props => props.accentColor || theme.primaryBlue};
  }

  .story-card-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .story-icon-container {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    font-size: 1.8rem;
    color: ${theme.white};
  }

  .story-title {
    font-weight: 700;
    font-size: 1.6rem;
    color: ${theme.darkText};
    margin-bottom: 0.5rem;
  }

  .story-content {
    margin-bottom: 1.5rem;
    color: ${theme.mediumText};
    line-height: 1.8;
  }

  .story-image-container {
    margin-top: 1.5rem;
    position: relative;
    overflow: hidden;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  }

  .story-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .story-image-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.6);
    color: ${theme.white};
    padding: 0.5rem 1rem;
    border-radius: 0 0 10px 10px;
    font-size: 0.9rem;
    font-weight: 500;
    text-align: center;
    width: 100%;
  }

  .story-quote {
    margin-top: 1.5rem;
    padding-left: 1.5rem;
    border-left: 4px solid ${theme.primaryBlue};
    color: ${theme.darkText};
    font-style: italic;
    font-size: 1.1rem;
    line-height: 1.7;
  }

  .story-info {
    margin-top: 1.5rem;
    padding-left: 1.5rem;
    border-left: 4px solid ${theme.primaryGreen};
    color: ${theme.darkText};
    font-size: 1.1rem;
    line-height: 1.7;
  }
`;

// Adicionar styled-components para fundo animado da equipe
const EquipeBgDecoration = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
  pointer-events: none;
`;
const EquipeCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: ${theme.primaryPink}10;
  &.equipe-circle-1 {
    width: 180px; height: 180px; top: 10%; left: 8%; background: ${theme.primaryPink}15;
  }
  &.equipe-circle-2 {
    width: 120px; height: 120px; top: 60%; right: 12%; background: ${theme.primaryBlue}15;
  }
  &.equipe-circle-3 {
    width: 90px; height: 90px; bottom: 18%; left: 18%; background: ${theme.primaryGreen}12;
  }
`;
const EquipeFloatingShape = styled(motion.div)`
  position: absolute;
  z-index: 1;
  &.equipe-shape-star {
    top: 22%; right: 18%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: ${theme.primaryYellow}; font-size: 1.2rem;
  }
  &.equipe-shape-circle {
    bottom: 28%; left: 12%; width: 22px; height: 22px; border-radius: 50%; background: ${theme.primaryOrange}25;
  }
`;

// Adicionar styled-components para fundo animado dos cards
const CardBgDecoration = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 16px;
`;
const CardCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
  &.card-circle-1 {
    width: 80px; height: 80px; top: 10%; right: 10%; background: ${theme.primaryPink};
  }
  &.card-circle-2 {
    width: 60px; height: 60px; bottom: 15%; left: 8%; background: ${theme.primaryBlue};
  }
  &.card-circle-3 {
    width: 40px; height: 40px; top: 60%; left: 15%; background: ${theme.primaryGreen};
  }
`;
const CardFloatingShape = styled(motion.div)`
  position: absolute;
  z-index: 1;
  opacity: 0.15;
  &.card-shape-star {
    top: 20%; left: 20%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; color: ${theme.primaryYellow}; font-size: 0.9rem;
  }
  &.card-shape-circle {
    bottom: 25%; right: 15%; width: 15px; height: 15px; border-radius: 50%; background: ${theme.primaryOrange};
  }
`;

const Home = () => {
  const phrases = [
    "Onde cada criança é uma estrela.",
    "A educação que transforma.",
    "Preparando o futuro com carinho."
  ];

  const [currentPhrase, setCurrentPhrase] = React.useState('');
  const [phraseIndex, setPhraseIndex] = React.useState(0);
  const [charIndex, setCharIndex] = React.useState(0);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const heroSubtitleRef = useRef(null);

  useEffect(() => {
    const typeSpeed = 70; // Tempo de digitação
    const deleteSpeed = 50; // Tempo de deleção
    const delayBetweenPhrases = 1500; // Atraso entre as frases

    let typingTimeout;

    const handleTyping = () => {
      const current = phrases[phraseIndex];
      if (!isDeleting) {
        setCurrentPhrase(current.substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);

        if (charIndex + 1 === current.length) {
          setIsDeleting(true);
          typingTimeout = setTimeout(() => { },
            delayBetweenPhrases);
        }
      } else {
        setCurrentPhrase(current.substring(0, charIndex - 1));
        setCharIndex(prev => prev - 1);

        if (charIndex - 1 === 0) {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    };

    typingTimeout = setTimeout(handleTyping,
      isDeleting ? deleteSpeed : typeSpeed
    );

    return () => clearTimeout(typingTimeout);
  }, [charIndex, isDeleting, phraseIndex, phrases]);

  const handleRipple = (e) => {
    const button = e.currentTarget;
    const circle = document.createElement("span");
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${e.clientX - (button.offsetLeft + radius)}px`;
    circle.style.top = `${e.clientY - (button.offsetTop + radius)}px`;
    circle.classList.add("ripple");

    const ripple = button.getElementsByClassName("ripple")[0];

    if (ripple) {
      ripple.remove();
    }

    button.appendChild(circle);
  };

  const missionData = {
    mission: MISSION,
    vision: VISION,
    values: VALUES
  };

  const highlightsData = [
    {
      title: 'Metodologias Ativas',
      description: 'Aprendizagem prática e participativa que estimula o pensamento crítico.',
      color: 'orange' // Use string for color to map to CSS class
    },
    {
      title: 'Ambiente Acolhedor',
      description: 'Espaço seguro e afetivo que valoriza e respeita cada criança.',
      color: 'pink'
    },
    {
      title: 'Educação Transformadora',
      description: 'Formação integral que prepara para fazer a diferença no mundo.',
      color: 'green'
    },
    {
      title: 'Desenvolvimento Individual',
      description: 'Atenção personalizada, respeitando o ritmo e potencial de cada criança.',
      color: 'blue'
    }
  ];

  // Funções auxiliares para gerenciar cores e ícones
  const getHighlightColor = (color) => {
    switch (color) {
      case 'pink': return theme.primaryPink;
      case 'blue': return theme.primaryBlue;
      case 'green': return theme.primaryGreen;
      case 'orange': return theme.primaryOrange;
      default: return theme.primaryBlue;
    }
  };

  const getHighlightIcon = (color) => {
    switch (color) {
      case 'pink': return 'bi bi-heart-fill';
      case 'blue': return 'bi bi-lightbulb-fill';
      case 'green': return 'bi bi-graph-up-arrow';
      case 'orange': return 'bi bi-stars';
      default: return 'bi bi-star-fill';
    }
  };

  const getValueColor = (index) => {
    const colors = [theme.primaryBlue, theme.primaryPink, theme.primaryGreen,
    theme.primaryOrange, theme.primaryRed, theme.primaryYellow];
    return colors[index % colors.length];
  };

  const getValueIcon = (index) => {
    const icons = ['bi bi-trophy-fill', 'bi bi-heart-fill', 'bi bi-people-fill',
      'bi bi-lightbulb-fill', 'bi bi-book-fill', 'bi bi-globe-americas'];
    return icons[index % icons.length];
  };

  const teamMembers = [
    { name: 'Mário Piedade', role: 'Director', photo: marioPiedade },
    { name: 'Elizabete Veloso', role: 'Coordenadora Ensino Primário', photo: elizabeteVeloso },
    { name: 'Carla Campos', role: 'Coordenadora Iº Ciclo', photo: carlaCampos },
  ];


  return (
    <>
      <motion.section
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-decoration-circles">
          <div className="hero-circle hero-circle-1"></div>
          <div className="hero-circle hero-circle-2"></div>
          <div className="hero-circle hero-circle-3"></div>
          <motion.div
            className="floating-shape shape-star"
            animate={{
              y: [0, -15, 0],
              rotate: [0, 10, 0]
            }}
            transition={{
              repeat: Infinity,
              duration: 5,
              ease: "easeInOut"
            }}
          >
            <i className="bi bi-star-fill"></i>
          </motion.div>
          <motion.div
            className="floating-shape shape-circle"
            animate={{
              y: [0, 20, 0],
              x: [0, 10, 0]
            }}
            transition={{
              repeat: Infinity,
              duration: 7,
              ease: "easeInOut"
            }}
          ></motion.div>
        </div>

        <div className="hero-content-h">
          <motion.h1
            className="hero-title-h"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <span className="text-gradient">Educar é Criar Laços</span> <br />
            <span className="text-white">E dar Afectos</span>
          </motion.h1>

          <motion.h4
            className="hero-subtitle"
            ref={heroSubtitleRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            {currentPhrase}
            <span className="blinking-cursor">|</span>
          </motion.h4>

          <motion.p
            className="hero-description-h"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          >
            No Talatona Kids, cada criança é única e especial. Nossa missão é nutrir talentos,
            desenvolver habilidades e criar memórias que durarão para toda a vida.
          </motion.p>

          <motion.div
            className="hero-cta-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
          >
            <a
              href="/pre-registration"
              className="hero-cta primary"
              onClick={handleRipple}
            >
              <i className="bi bi-pencil-square me-2"></i>
              Inscreva-se Já!
            </a>

            <a
              href="/offer"
              className="hero-cta secondary"
            >
              <i className="bi bi-info-circle me-2"></i>
              Conheça Nossos Programas
            </a>
          </motion.div>

          {/* 
          <motion.div
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
          >
            <motion.div
              className="hero-stat-item"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="stat-icon-container">
                <i className="bi bi-calendar-check-fill"></i>
              </div>
              <span className="hero-stat-number">10+</span>
              <span className="hero-stat-text">Anos de Experiência</span>
            </motion.div>
            <motion.div
              className="hero-stat-item"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="stat-icon-container">
                <i className="bi bi-mortarboard-fill"></i>
              </div>
              <span className="hero-stat-number">200+</span>
              <span className="hero-stat-text">Alunos Formados</span>
            </motion.div>
            <motion.div
              className="hero-stat-item"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="stat-icon-container">
                <i className="bi bi-emoji-smile-fill"></i>
              </div>
              <span className="hero-stat-number">98%</span>
              <span className="hero-stat-text">Pais Satisfeitos</span>
            </motion.div>
          </motion.div>
          */}
        </div>

        {/* 
        <motion.div 
          className="hero-visual-container"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
        >
          <div className="hero-visual">
            <img 
              src={kidsDancing2}
              alt="Crianças dançando na Talatona Kids"
              className="hero-main-image"
            />
            <div className="hero-visual-floating">
              <motion.div 
                className="floating-badge safety"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.2 } }}
              >
                <i className="bi bi-shield-check"></i>
                <span>Ambiente Seguro</span>
              </motion.div>
              <motion.div 
                className="floating-badge qualified"
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.2 } }}
              >
                <i className="bi bi-mortarboard-fill"></i>
                <span>Professores Qualificados</span>
              </motion.div>
              <motion.div 
                className="floating-badge care"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.6 }}
                whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.2 } }}
              >
                <i className="bi bi-heart-fill"></i>
                <span>Carinho e Atenção</span>
              </motion.div>
            </div>
          </div>
        </motion.div>
       */}
      </motion.section>

      {/* MISSÃO, VISÃO, VALORES */}
      <SectionContainer id="missao-valores">
        <div className="row mb-5">
          <div className="col-lg-6">
            <SectionTitle accentColor={theme.primaryBlue}>
              Nossa História e Propósito
            </SectionTitle>
            <p className="section-intro text-muted lead">
              No Talatona Kids, acreditamos que cada criança é única e possui um potencial ilimitado.
              Nossa abordagem educacional é baseada em valores humanos, desenvolvimento integral e na
              criação de um ambiente estimulante onde as crianças possam florescer.
            </p>
          </div>
          <div className="col-lg-6 d-flex justify-content-center align-items-center">
            <motion.div
              className="purpose-image-container"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={ourSpace2}
                alt="Nosso espaço Talatona Kids"
                className="purpose-image"
              />
              <div className="purpose-decoration"></div>
            </motion.div>
          </div>
        </div>

        <div className="row mvv-container">
          <div className="col-md-6 mb-4">
            <StoryCard
              accentColor={theme.primaryPink}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="shadow"
            >
              <div className="story-card-header">
                <div className="story-icon-container" style={{ background: theme.primaryPink }}>
                  <i className="bi bi-bullseye"></i>
                </div>
                <h3 className="story-title">Nossa Missão</h3>
              </div>
              <p className="story-content text-muted">{missionData.mission}</p>
              <motion.div
                className="story-image-container"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <img src={ourSpace4} alt="Nossa Missão - Talatona Kids" className="story-image" />
              </motion.div>
            </StoryCard>
          </div>

          <div className="col-md-6 mb-4">
            <StoryCard
              accentColor={theme.primaryBlue}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="shadow"
            >
              <div className="story-card-header">
                <div className="story-icon-container" style={{ background: theme.primaryBlue }}>
                  <i className="bi bi-binoculars-fill"></i>
                </div>
                <h3 className="story-title">Nossa Visão</h3>
              </div>
              <p className="story-content text-muted">{missionData.vision}</p>
              <motion.div
                className="story-image-container"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <img src={kidsAfrica2} alt="Nossa Visão - Talatona Kids" className="story-image" />
              </motion.div>
            </StoryCard>
          </div>
        </div>

   {/* SEÇÃO: CONHEÇA NOSSA EQUIPE */}
      <SectionContainer id="nossa-equipe">
        <div className="team-header text-center mb-5">
          <SectionTitle accentColor={theme.primaryBlue} className="text-center mx-auto">
            As Pessoas Por Trás do Nosso Sucesso
          </SectionTitle>
        </div>

        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <motion.div
              className="team-member-card"
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <img src={member.photo} alt={member.name} className="team-member-photo" />
              <h4 className="team-member-name">{member.name}</h4>
              <p className="team-member-role">{member.role}</p>
            </motion.div>
          ))}
        </div>
      </SectionContainer>

        {/* SEÇÃO DE VALORES - SUBSTITUIR O BLOCO EXISTENTE */}
        <div className="row mt-5">
          <div className="col-lg-12">
            <SectionTitle accentColor={theme.primaryGreen}>
              Nossos Valores
            </SectionTitle>
          </div>
        </div>

        {/* Usando div's para controlar o layout de forma mais explícita */}
        <div className="container">
          {/* Primeira linha - 3 cartões centralizados */}
          <div className="row justify-content-center">
            {missionData.values.slice(0, 3).map((value, index) => (
                <div className="col-md-4 col-lg-3 mb-4" key={index}>
                  <motion.div
                      className="value-card shadow-sm"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                  >
                    <div className="value-icon" style={{ backgroundColor: getValueColor(index) }}>
                      <i className={getValueIcon(index)}></i>
                    </div>
                    <h5 className="value-title">{value}</h5>
                  </motion.div>
                </div>
            ))}
          </div>

          {/* Segunda linha - 2 cartões centralizados */}
          {missionData.values.length > 3 && (
              <div className="row justify-content-center">
                {missionData.values.slice(3, 5).map((value, index) => (
                    <div className="col-md-4 col-lg-3 mb-4" key={index + 3}>
                      <motion.div
                          className="value-card shadow-sm"
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true, amount: 0.2 }}
                          transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                          whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                      >
                        <div className="value-icon" style={{ backgroundColor: getValueColor(index + 3) }}>
                          <i className={getValueIcon(index + 3)}></i>
                        </div>
                        <h5 className="value-title">{value}</h5>
                      </motion.div>
                    </div>
                ))}
              </div>
          )}
        </div>

      </SectionContainer>

      {/* DESTAQUES/DIFERENCIAIS */}
      <SectionContainer style={{ backgroundColor: theme.lightGray }} id="destaques">
        <div className="highlights-header">
          <SectionTitle accentColor={theme.primaryPink}>
            Por Que Escolher a Talatona Kids?
          </SectionTitle>
          <p className="section-intro">
            Nossa escola se destaca por oferecer uma experiência educacional completa,
            combinando excelência acadêmica com desenvolvimento socioemocional em um
            ambiente seguro e estimulante para cada criança.
          </p>
        </div>

        <div className="row">
          {highlightsData.map((highlight, index) => (
            <div className="col-md-6 col-lg-3 mb-4" key={index}>
              <motion.div
                className="highlight-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{
                  y: -10,
                  boxShadow: '0 15px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div className="highlight-shape" style={{ backgroundColor: getHighlightColor(highlight.color) }}></div>
                <div className="highlight-icon-container">
                  <div className="highlight-icon" style={{ backgroundColor: getHighlightColor(highlight.color) }}>
                    <i className={getHighlightIcon(highlight.color)}></i>
                  </div>
                </div>
                <h4 className="highlight-title">{highlight.title}</h4>
                <p className="highlight-description text-muted">{highlight.description}</p>
                <a href="/offer" className="highlight-link">
                  Saiba mais <i className="bi bi-arrow-right"></i>
                </a>
              </motion.div>
            </div>
          ))}
        </div>

        <motion.div
          className="highlights-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <a href="/offer" className="btn-learn-more">
            Conheça Todos os Nossos Diferenciais
            <i className="bi bi-arrow-right-circle ms-2"></i>
          </a>
        </motion.div>
      </SectionContainer>

   
        <div className="row g-4 justify-content-center">
          <div className="col-lg-8">
            <StoryCard
              accentColor={theme.white}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="shadow"
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              <CardBgDecoration>
                <CardCircle className="card-circle-1" />
                <CardCircle className="card-circle-2" />
                <CardFloatingShape
                  className="card-shape-star"
                  animate={{ y: [0, -8, 0], rotate: [0, 8, 0] }}
                  transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                >
                  <i className="bi bi-star-fill"></i>
                </CardFloatingShape>
              </CardBgDecoration>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div className="story-card-header">
                  <div className="story-icon-container" style={{ background: theme.primaryBlue }}>
                    <i className="bi bi-lightbulb-fill"></i>
                  </div>
                  <h3 className="story-title mb-0">Estrutura Pedagógica</h3>
                </div>
                <p className="story-content text-muted mb-0">
                  O Talatona Kids foi concebido por uma equipa multidisciplinar formada por pedagogos, médicos, arquitetos e engenheiros, com foco em construir um ambiente escolar seguro, funcional e alinhado com as exigências do século XXI. A infraestrutura é pensada para apoiar o desenvolvimento integral da criança, desde os primeiros anos até a transição para o Ensino Primário.                </p>
              </div>
            </StoryCard>
          </div>
          <div className="col-lg-6">
            <StoryCard
              accentColor={theme.white}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="shadow"
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              <CardBgDecoration>
                <CardCircle className="card-circle-1" />
                <CardCircle className="card-circle-3" />
                <CardFloatingShape
                  className="card-shape-circle"
                  animate={{ y: [0, 10, 0], x: [0, 6, 0] }}
                  transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
                />
              </CardBgDecoration>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div className="story-card-header">
                  <div className="story-icon-container" style={{ background: theme.primaryPink }}>
                    <i className="bi bi-people-fill"></i>
                  </div>
                  <h3 className="story-title mb-0">Coordenação Educacional</h3>
                </div>
                <p className="story-content text-muted mb-2">
                  A direção pedagógica é composta por profissionais com formação sólida em educação, psicologia e saúde infantil. A proposta do Talatona Kids vai além do ensino tradicional, integrando conhecimento, bem-estar emocional e desenvolvimento físico num único projeto educativo.
                </p>
                <div className="story-quote mt-3">
                  <i className="bi bi-quote"></i>
                  <span className="fst-italic ms-2">"O projeto nasceu com o objetivo de oferecer mais do que uma escola — um espaço de crescimento emocional, físico e intelectual", explicou a fundadora à Platinaline (2022).</span>
                </div>
              </div>
            </StoryCard>
          </div>
          <div className="col-lg-6">
            <StoryCard
              accentColor={theme.white}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="shadow"
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              <CardBgDecoration>
                <CardCircle className="card-circle-2" />
                <CardCircle className="card-circle-3" />
                <CardFloatingShape
                  className="card-shape-star"
                  animate={{ y: [0, -6, 0], rotate: [0, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                >
                  <i className="bi bi-star-fill"></i>
                </CardFloatingShape>
              </CardBgDecoration>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div className="story-card-header">
                  <div className="story-icon-container" style={{ background: theme.primaryGreen }}>
                    <i className="bi bi-mortarboard-fill"></i>
                  </div>
                  <h3 className="story-title mb-0">Corpo Docente e Acompanhamento</h3>
                </div>
                <p className="story-content text-muted mb-2">
                  A equipa de professores inclui especialistas em Educação Infantil, Ensino Primário e áreas complementares como inglês, música, educação física, ballet e capoeira. O ensino de línguas é baseado em imersão prática, com situações do cotidiano que facilitam a aprendizagem desde cedo. As turmas são organizadas conforme as etapas do desenvolvimento infantil, com número reduzido de alunos por sala e suporte psicopedagógico contínuo.
                </p>
              </div>
            </StoryCard>
          </div>
        </div>
      

      {/* SEÇÃO: DEPOIMENTOS */}
      <SectionContainer className="testimonials-section" id="depoimentos">
        <div className="testimonials-bg-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>

        <div className="testimonials-header text-center">
          <SectionTitle accentColor={theme.primaryGreen} className="text-center mx-auto">
            O Que as Famílias Dizem Sobre Nós
          </SectionTitle>
          <p className="section-intro text-center mx-auto" style={{ maxWidth: '700px' }}>
            As famílias reconhecem nosso compromisso com o desenvolvimento pleno das crianças. Destacam a qualidade do ensino, o carinho dos educadores e a variedade de atividades que contribuem para a evolução cognitiva e emocional dos alunos.
          </p>
        </div>

        <div className="row testimonials-container">
          <div className="col-md-6 col-lg-4 mb-4">
            <motion.div
              className="testimonial-card shadow"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -10, boxShadow: '0 15px 30px rgba(0,0,0,0.1)' }}
            >
              <div className="testimonial-quote-icon">
                <i className="bi bi-quote"></i>
              </div>
              <p className="testimonial-text text-muted">
                “Foi a melhor escolha para o meu filho. A estrutura é excelente, e os professores são super atenciosos. Ele chega todos os dias contando o que aprendeu.”
              </p>
              <div className="testimonial-author">
                <div className="testimonial-info">
                  <h5 className="testimonial-name">Mãe de aluno</h5>
                  <p className="testimonial-relation text-muted">Talatona</p>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="col-md-6 col-lg-4 mb-4">
            <motion.div
              className="testimonial-card shadow"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -10, boxShadow: '0 15px 30px rgba(0,0,0,0.1)' }}
            >
              <div className="testimonial-quote-icon">
                <i className="bi bi-quote"></i>
              </div>
              <p className="testimonial-text text-muted">
                “Adorei saber que oferecem atividades como inglês e capoeira logo na infância. Está a fazer diferença no desenvolvimento motor e na comunicação da minha filha.”
              </p>
              <div className="testimonial-author">
                <div className="testimonial-info">
                  <h5 className="testimonial-name">Pai de aluna</h5>
                  <p className="testimonial-relation text-muted">Belas</p>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="col-md-6 col-lg-4 mb-4">
            <motion.div
              className="testimonial-card shadow"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ y: -10, boxShadow: '0 15px 30px rgba(0,0,0,0.1)' }}
            >
              <div className="testimonial-quote-icon">
                <i className="bi bi-quote"></i>
              </div>
              <p className="testimonial-text text-muted">
                “O espaço é seguro, bem pensado, e os educadores são muito pacientes. Confio plenamente no trabalho deles.”
              </p>
              <div className="testimonial-author">
                <div className="testimonial-info">
                  <h5 className="testimonial-name">Encarregada de educação</h5>
                  <p className="testimonial-relation text-muted">Via WhatsApp institucional</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </SectionContainer>
    </>
  );
};

export default Home;